import React, { useState } from "react";
import { resolveAgentHeading } from "../../shared/consumer-language";

interface TopBarProps {
  agentName: string;
  notificationCount: number;
  sseConnected: boolean;
  onSettingsClick: () => void;
  onSearchClick?: () => void;
  onProfileClick?: () => void;
  onSidebarToggle: () => void;
  sidebarCollapsed: boolean;
  theme: "dark" | "light";
  onThemeToggle: () => void;
  isMobile?: boolean;
}

export default function TopBar({
  agentName,
  notificationCount,
  sseConnected,
  onSettingsClick,
  onSearchClick,
  onProfileClick,
  onSidebarToggle,
  sidebarCollapsed,
  theme,
  onThemeToggle,
  isMobile = false,
}: TopBarProps): React.ReactElement {
  const [searchOpen, setSearchOpen] = useState(false);
  const visibleAgentName = resolveAgentHeading(agentName);

  return (
    <header
      className="flex items-center justify-between h-12 px-4 shrink-0"
      style={{
        backgroundColor: "var(--c-surface)",
        borderBottom: "1px solid var(--c-border)",
      }}
    >
      {/* Left: sidebar toggle + agent name */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onSidebarToggle}
          className="p-1.5 rounded-md transition-colors shrink-0"
          style={{ color: "var(--c-text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          title={sidebarCollapsed ? "Show panel" : "Hide panel"}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M3 4.5h12M3 9h12M3 13.5h12" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          {/* Agent status dot */}
          <span className="relative flex h-2 w-2">
            {sseConnected && (
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-30"
                style={{ backgroundColor: "var(--c-success)" }}
              />
            )}
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ backgroundColor: sseConnected ? "var(--c-success)" : "var(--c-text-faint)" }}
            />
          </span>
          <span
            className="text-sm font-semibold truncate"
            data-agent-name="true"
            style={{ color: "var(--c-text)" }}
          >
            {visibleAgentName}
          </span>
        </div>
      </div>

      {/* Center: search (hidden on mobile) */}
      {!isMobile && (
        <div className="flex-1 max-w-md mx-8">
          {searchOpen ? (
            <input
              autoFocus
              type="text"
              placeholder="Search conversations, files, artifacts..."
              className="w-full px-3 py-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--c-primary)]"
              style={{
                backgroundColor: "var(--c-input-bg)",
                color: "var(--c-text)",
                border: "1px solid var(--c-border)",
              }}
              onBlur={() => setSearchOpen(false)}
              onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)}
            />
          ) : (
            <button
              onClick={() => {
                setSearchOpen(true);
                onSearchClick?.();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors"
              style={{
                backgroundColor: "var(--c-surface-2)",
                color: "var(--c-text-faint)",
                border: "1px solid var(--c-border)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--c-text-faint)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--c-border)")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="6" cy="6" r="4.5" />
                <path d="M9.5 9.5L13 13" />
              </svg>
              Search...
            </button>
          )}
        </div>
      )}

      {/* Right: actions */}
      <div className="flex items-center gap-1">
        {/* Theme toggle */}
        <button
          onClick={onThemeToggle}
          className="p-2 rounded-md transition-colors"
          style={{ color: "var(--c-text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            {theme === "dark" ? (
              <circle cx="8" cy="8" r="4" />
            ) : (
              <path d="M13 8.5a5.5 5.5 0 01-7-7 5.5 5.5 0 107 7z" />
            )}
          </svg>
        </button>

        {/* Settings */}
        <button
          onClick={onSettingsClick}
          className="p-2 rounded-md transition-colors"
          style={{ color: "var(--c-text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          title="Settings"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M6.8 1h2.4l.4 2.2a5.5 5.5 0 011.4.8l2.1-.8 1.2 2.1-1.7 1.4a5.5 5.5 0 010 1.6l1.7 1.4-1.2 2.1-2.1-.8a5.5 5.5 0 01-1.4.8L9.2 15H6.8l-.4-2.2a5.5 5.5 0 01-1.4-.8l-2.1.8-1.2-2.1 1.7-1.4a5.5 5.5 0 010-1.6L1.7 6.3l1.2-2.1 2.1.8a5.5 5.5 0 011.4-.8L6.8 1z" />
            <circle cx="8" cy="8" r="2" />
          </svg>
        </button>

        {/* Notifications */}
        <button
          onClick={onProfileClick}
          className="relative p-2 rounded-md transition-colors"
          style={{ color: "var(--c-text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          title="Notifications"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M4 6a4 4 0 018 0v2.5l1.5 2.5H2.5L4 8.5V6z" />
            <path d="M6 13a2 2 0 004 0" />
          </svg>
          {notificationCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold"
              style={{
                backgroundColor: "var(--c-danger)",
                color: "white",
              }}
            >
              {notificationCount > 99 ? "99+" : notificationCount}
            </span>
          )}
        </button>

        {/* Profile */}
        <button
          className="ml-1 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
          style={{
            backgroundColor: "var(--c-primary-subtle)",
            color: "var(--c-primary)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          title="Profile"
        >
          U
        </button>
      </div>
    </header>
  );
}
